import * as React from "react";
import {
  Grid,
  TextField,
  Button,
  CircularProgress,
  Link,
  InputLabel,
  Backdrop,
  Stack,
} from "@mui/material";
import { normalizeWhitespaceForSave } from "../utils/WhitespaceHelper";
import { AuthenticationContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { useHistory } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useFlash } from "../FlashContext";

// Constant for maximum lengths
const HEADING_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;

const LoginPageContent: React.FC = (props: any) => {
  const history = useHistory();
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const [loading, setLoading] = React.useState<boolean>(true);
  const [selectedImage, setSelectedImage] = React.useState<File | null>(null);

  const [loginContentData, setLoginContentData] = React.useState({
    heading1: "",
    description1: "",
    heading2: "",
    description2: "",
    heading3: "",
    description3: "",
    imageUrl: "",
  });

  React.useEffect(() => {
    fetchLoginPageContent();
  }, []);

  const fetchLoginPageContent = () => {
    setLoading(true);
    fetch(`/login_page_content`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success) {
          const loginContent = result.resource;
          setLoginContentData({
            heading1: loginContent.heading1,
            heading2: loginContent.heading2,
            heading3: loginContent.heading3,
            description1: loginContent.subheading1,
            description2: loginContent.subheading2,
            description3: loginContent.subheading3,
            imageUrl: loginContent.image_url,
          });
        } else {
          showToast({
            title: "Load Failed",
            text: "Failed to load login page content",
            type: "error",
          });
        }
        setLoading(false);
      })
      .catch((error) => {
        showToast({
          title: "Load Failed",
          text: "Error fetching login page content",
          type: "error",
        });
        setLoading(false);
      });
  };

  const handleLoginContentChange =
    (field: keyof typeof loginContentData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLoginContentData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  const handleSave = () => {
    if (!loginContentData) return;

    const formData = new FormData();

    formData.append(
      "login_page_content[heading1]",
      normalizeWhitespaceForSave(loginContentData.heading1)
    );
    formData.append(
      "login_page_content[heading2]",
      normalizeWhitespaceForSave(loginContentData.heading2)
    );
    formData.append(
      "login_page_content[heading3]",
      normalizeWhitespaceForSave(loginContentData.heading3)
    );
    formData.append(
      "login_page_content[subheading1]",
      normalizeWhitespaceForSave(loginContentData.description1)
    );
    formData.append(
      "login_page_content[subheading2]",
      normalizeWhitespaceForSave(loginContentData.description2)
    );
    formData.append(
      "login_page_content[subheading3]",
      normalizeWhitespaceForSave(loginContentData.description3)
    );

    if (selectedImage) {
      formData.append("login_page_content[image]", selectedImage);
    }

    const headers = {
      "X-CSRF-Token": authenticationSetting.csrfToken,
    };

    setLoading(true);

    fetch(`/login_page_content`, {
      method: "PUT",
      headers,
      body: formData,
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          showToast({
            title: "Save Failed",
            text: "Failed to update login content",
            type: "error",
          });
          setLoading(false);
          return;
        }

        const loginContent = result.resource;

        setLoginContentData({
          heading1: loginContent.heading1,
          heading2: loginContent.heading2,
          heading3: loginContent.heading3,
          description1: loginContent.subheading1,
          description2: loginContent.subheading2,
          description3: loginContent.subheading3,
          imageUrl: loginContent.image_url,
        });

        setSelectedImage(null);

        showToast({
          title: "Successfully Updated",
          text: "Login page content updated successfully",
          type: "success",
        });

        setLoading(false);
      })
      .catch(() => {
        showToast({
          title: "Save Failed",
          text: "An error occurred while saving data",
          type: "error",
        });
        setLoading(false);
      });
  };

  return (
    <div className="main-content-outer">
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        className="main-content"
        sx={{ paddingLeft: "138px" }}
      >
        <Grid
          item
          xs={12}
          id="provider-action-report"
          className="patient-edit-container patient-edit-form provider-action-report new-questionnaire-container"
          style={{
            maxHeight: "calc(100vh - 120px)",
            height: "calc(100vh - 120px)",
          }}
        >
          <Grid container>
            <Grid
              className="patient-edit-header"
              container
              justifyContent="space-between"
            >
              <Grid item xs={4}>
                <Stack direction="row" columnGap="12px" alignItems="center">
                  <Link
                    style={{
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                    }}
                    href={`/settings`}
                  >
                    <ArrowBackIosNewIcon
                      style={{
                        borderRadius: "3px",
                        padding: "8px",
                        color: "black",
                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        fontSize: "14px",
                      }}
                    />
                  </Link>
                  <p className="secondary-label" style={{ marginLeft: "0px" }}>
                    Login Page Content Management
                  </p>
                </Stack>
              </Grid>

              <Grid item xs={6} className="q-btn-container">
                <Button
                  className="orange-btn"
                  onClick={handleSave}
                  style={{
                    height: "40px",
                    marginRight: "16px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                  }}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={3}>
                <Grid container className="form-container">
                  <Grid
                    item
                    xs={12}
                    className="patient-info-left-container form-left-container"
                    style={{
                      marginLeft: "30px",
                    }}
                  >
                    <Grid container>
                      <Grid item xs={12}>
                        <InputLabel
                          className="field-label"
                          style={{ color: "#000000", fontWeight: 700 }}
                        >
                          Heading 1*
                        </InputLabel>
                      </Grid>
                      <Grid item xs={12} className="field-container">
                        <TextField
                          size="small"
                          value={loginContentData.heading1}
                          className="the-field"
                          required
                          variant="outlined"
                          placeholder="Enter Heading 1"
                          error={loginContentData.heading1.length > HEADING_MAX_LENGTH}
                          helperText={
                            loginContentData.heading1.length > HEADING_MAX_LENGTH
                              ? `Maximum length is ${HEADING_MAX_LENGTH} characters.`
                              : ""
                          }
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                          ) => {
                            let input = event.target.value;
                            if (input.length <= HEADING_MAX_LENGTH) {
                              handleLoginContentChange("heading1")({
                                ...event,
                                target: { ...event.target, value: input },
                              } as React.ChangeEvent<HTMLInputElement>);
                            }
                          }}
                          inputProps={{
                            maxLength: HEADING_MAX_LENGTH,
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Grid container>
                      <Grid item xs={12}>
                        <InputLabel
                          className="field-label"
                          style={{ color: "#000000", fontWeight: 700 }}
                        >
                          Description 1*
                        </InputLabel>
                      </Grid>
                      <Grid item xs={12} className="field-container">
                        <TextField
                          size="small"
                          value={loginContentData.description1}
                          className="the-field"
                          required
                          multiline
                          rows={4}
                          variant="outlined"
                          placeholder="Enter Description 1"
                          error={loginContentData.description1.length > DESCRIPTION_MAX_LENGTH}
                          helperText={
                            loginContentData.description1.length > DESCRIPTION_MAX_LENGTH
                              ? "Maximum length is 500 characters."
                              : ""
                          }
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                          ) => {
                            let input = event.target.value;
                            if (input.length <= DESCRIPTION_MAX_LENGTH) {
                              handleLoginContentChange("description1")({
                                ...event,
                                target: { ...event.target, value: input },
                              } as React.ChangeEvent<HTMLInputElement>);
                            }
                          }}
                          inputProps={{
                            maxLength: DESCRIPTION_MAX_LENGTH,
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              padding: "0px !important",
                            },
                            "& .MuiInputBase-inputMultiline": {
                              padding: "10.5px !important",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={3}>
                <Grid container className="form-container">
                  <Grid
                    item
                    xs={12}
                    className="patient-info-left-container form-left-container"
                    style={{
                      marginLeft: "30px",
                    }}
                  >
                    <Grid container>
                      <Grid item xs={12}>
                        <InputLabel
                          className="field-label"
                          style={{ color: "#000000", fontWeight: 700 }}
                        >
                          Heading 2*
                        </InputLabel>
                      </Grid>
                      <Grid item xs={12} className="field-container">
                        <TextField
                          size="small"
                          value={loginContentData.heading2}
                          className="the-field"
                          required
                          variant="outlined"
                          placeholder="Enter Heading 2"
                          error={loginContentData.heading2.length > HEADING_MAX_LENGTH}
                          helperText={
                            loginContentData.heading2.length > HEADING_MAX_LENGTH
                              ? "Maximum length is 100 characters."
                              : ""
                          }
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                          ) => {
                            let input = event.target.value;
                            if (input.length <= HEADING_MAX_LENGTH) {
                              handleLoginContentChange("heading2")({
                                ...event,
                                target: { ...event.target, value: input },
                              } as React.ChangeEvent<HTMLInputElement>);
                            }
                          }}
                          inputProps={{
                            maxLength: HEADING_MAX_LENGTH,
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Grid container>
                      <Grid item xs={12}>
                        <InputLabel
                          className="field-label"
                          style={{ color: "#000000", fontWeight: 700 }}
                        >
                          Description 2*
                        </InputLabel>
                      </Grid>
                      <Grid item xs={12} className="field-container">
                        <TextField
                          size="small"
                          value={loginContentData.description2}
                          className="the-field"
                          required
                          multiline
                          rows={4}
                          variant="outlined"
                          placeholder="Enter Description 2"
                          error={loginContentData.description2.length > DESCRIPTION_MAX_LENGTH}
                          helperText={
                            loginContentData.description2.length > DESCRIPTION_MAX_LENGTH
                              ? "Maximum length is 500 characters."
                              : ""
                          }
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                          ) => {
                            let input = event.target.value;
                            if (input.length <= DESCRIPTION_MAX_LENGTH) {
                              handleLoginContentChange("description2")({
                                ...event,
                                target: { ...event.target, value: input },
                              } as React.ChangeEvent<HTMLInputElement>);
                            }
                          }}
                          inputProps={{
                            maxLength: DESCRIPTION_MAX_LENGTH,
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              padding: "0px !important",
                            },
                            "& .MuiInputBase-inputMultiline": {
                              padding: "10.5px !important",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={3}>
                <Grid container className="form-container">
                  <Grid
                    item
                    xs={12}
                    className="patient-info-left-container form-left-container"
                    style={{
                      marginLeft: "30px",
                    }}
                  >
                    <Grid container>
                      <Grid item xs={12}>
                        <InputLabel
                          className="field-label"
                          style={{ color: "#000000", fontWeight: 700 }}
                        >
                          Heading 3*
                        </InputLabel>
                      </Grid>
                      <Grid item xs={12} className="field-container">
                        <TextField
                          size="small"
                          value={loginContentData.heading3}
                          className="the-field"
                          required
                          variant="outlined"
                          placeholder="Enter Heading 3"
                          error={loginContentData.heading3.length > HEADING_MAX_LENGTH}
                          helperText={
                            loginContentData.heading3.length > HEADING_MAX_LENGTH
                              ? "Maximum length is 100 characters."
                              : ""
                          }
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                          ) => {
                            let input = event.target.value;
                            if (input.length <= HEADING_MAX_LENGTH) {
                              handleLoginContentChange("heading3")({
                                ...event,
                                target: { ...event.target, value: input },
                              } as React.ChangeEvent<HTMLInputElement>);
                            }
                          }}
                          inputProps={{
                            maxLength: HEADING_MAX_LENGTH,
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Grid container>
                      <Grid item xs={12}>
                        <InputLabel
                          className="field-label"
                          style={{ color: "#000000", fontWeight: 700 }}
                        >
                          Description 3*
                        </InputLabel>
                      </Grid>
                      <Grid item xs={12} className="field-container">
                        <TextField
                          size="small"
                          value={loginContentData.description3}
                          className="the-field"
                          required
                          multiline
                          rows={4}
                          variant="outlined"
                          placeholder="Enter Description 3"
                          error={loginContentData.description3.length > DESCRIPTION_MAX_LENGTH}
                          helperText={
                            loginContentData.description3.length > DESCRIPTION_MAX_LENGTH
                              ? "Maximum length is 500 characters."
                              : ""
                          }
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                          ) => {
                            let input = event.target.value;
                            if (input.length <= DESCRIPTION_MAX_LENGTH) {
                              handleLoginContentChange("description3")({
                                ...event,
                                target: { ...event.target, value: input },
                              } as React.ChangeEvent<HTMLInputElement>);
                            }
                          }}
                          inputProps={{
                            maxLength: DESCRIPTION_MAX_LENGTH,
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              padding: "0px !important",
                            },
                            "& .MuiInputBase-inputMultiline": {
                              padding: "10.5px !important",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={3}>
                <Grid container className="form-container">
                  <Grid
                    item
                    xs={12}
                    className="patient-info-left-container form-left-container"
                    style={{
                      marginLeft: "30px",
                    }}
                  >
                    <Grid container>
                      <Grid item xs={12}>
                        <InputLabel
                          className="field-label"
                          style={{ color: "#000000", fontWeight: 700 }}
                        >
                          Background Image
                        </InputLabel>
                      </Grid>
                      <Grid item xs={12}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          style={{ display: "none", height: "38px" }}
                          id="file-input"
                        />
                        <label htmlFor="file-input" style={{ width: "50%" }}>
                          <Button className="btn-import" component="span">
                            Select Image
                          </Button>
                        </label>
                      </Grid>
                    </Grid>

                    {loginContentData.imageUrl && (
                      <Grid container>
                        <Grid item xs={12}>
                          <InputLabel
                            className="field-label"
                            style={{ color: "#000000", fontWeight: 700 }}
                          >
                            Existing Image
                          </InputLabel>
                        </Grid>
                        <Grid item xs={12}>
                          <img
                            alt="Login Background"
                            src={loginContentData.imageUrl}
                            width="80%"
                          />
                        </Grid>
                      </Grid>
                    )}

                    {selectedImage && (
                      <Grid container>
                        <Grid item xs={12}>
                          <InputLabel
                            className="field-label"
                            style={{ color: "#000000", fontWeight: 700 }}
                          >
                            Selected Image Preview
                          </InputLabel>
                        </Grid>
                        <Grid item xs={12}>
                          <img
                            alt="Selected Preview"
                            src={URL.createObjectURL(selectedImage)}
                            width="80%"
                            onLoad={(e) =>
                              URL.revokeObjectURL(
                                (e.target as HTMLImageElement).src
                              )
                            }
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress />
      </Backdrop>
    </div>
  );
};

export default LoginPageContent;
